import { createSignal, onMount, For, createMemo, Show } from 'solid-js';
import { useSFetch } from '../hooks/sfetch';
import { User } from '../components/UserContextProvider';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { SearchInput } from '../components/SearchInput';
import styles from './Admin.module.css';

export default function Admin() {
    const navigate = useNavigate();
    const sfetch = useSFetch();
    const toast = useToast();
    const { t } = useTranslation();
    const [users, setUsers] = createSignal<User[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [searchTerm, setSearchTerm] = createSignal("");

    onMount(() => {
        sfetch('/api/User/Users').then(data => {
            setUsers(data.value ?? []);
            setLoading(false);
        });
    });

    const filteredUsers = createMemo(() => {
        const term = searchTerm().toLowerCase();
        return users().filter(user =>
            user.username.toLowerCase().includes(term)
            || (user.uuid ?? "").toLowerCase().includes(term)
            || user.id.toLowerCase().includes(term)
        );
    });

    const verifiedCount = createMemo(() => users().filter(u => u.minecraftUsernameVerified).length);

    function switchAllClasses(on: boolean) {
        const url = "/api/User/SwitchAllClasses/" + on;
        sfetch(url, { method: "POST" }).then((res) => {
            if (res.ok) {
                toast?.info(on ? t('ADMIN_CLASSES_ENABLED') : t('ADMIN_CLASSES_DISABLED'));
            } else {
                toast?.error(t('ADMIN_CLASSES_FAILED'));
            }
        });
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1><Text val="ADMIN_DASHBOARD" /></h1>
                    <span class={styles.subtitle}><Text val="ADMIN_USER_MANAGEMENT" /></span>
                </div>
                <span class={styles.subtitle}>
                    {users().length} <Text val="ADMIN_TOTAL_USERS" />
                </span>
            </div>

            <div class={styles.statRow}>
                <div class={`fcard ${styles.statCard}`}>
                    <div class={styles.statHeader}>
                        <i class="fa-solid fa-users" />
                        <Text val="ADMIN_TOTAL_USERS" />
                    </div>
                    <span class={styles.statValue}>{users().length}</span>
                </div>
                <div class={`fcard ${styles.statCard}`}>
                    <div class={styles.statHeader}>
                        <i class="fa-solid fa-circle-check" />
                        <Text val="VERIFIED" />
                    </div>
                    <span class={styles.statValue}>{verifiedCount()}</span>
                </div>
                <div class={`fcard ${styles.statCard}`}>
                    <div class={styles.statHeader}>
                        <i class="fa-solid fa-gauge" />
                        <Text val="ADMIN_SYSTEM_LOAD" />
                    </div>
                    <span class={styles.statValue}>&lt; 25%</span>
                </div>
                <div class={`fcard ${styles.statCard}`}>
                    <div class={styles.statHeader}>
                        <i class="fa-solid fa-shield-halved" />
                        <Text val="ADMIN_SECURITY_ALERTS" />
                    </div>
                    <span class={styles.statValue}>0</span>
                </div>
            </div>

            <div class={`fcard ${styles.section}`}>
                <h2><Text val="ADMIN_CLASS_CONTROL" /></h2>
                <div class="h wrap" style={{ gap: 'var(--spacing-sm)' }}>
                    <button class="btn btn-primary" onClick={() => switchAllClasses(true)}>
                        <i class="fa-solid fa-toggle-on" /> <Text val="ADMIN_ENABLE_ALL_CLASSES" />
                    </button>
                    <button class="btn btn-secondary" onClick={() => switchAllClasses(false)}>
                        <i class="fa-solid fa-toggle-off" /> <Text val="ADMIN_DISABLE_ALL_CLASSES" />
                    </button>
                </div>
            </div>

            <div class={styles.section}>
                <h2><Text val="USERS" /></h2>
                <div class={styles.toolbar}>
                    <SearchInput
                        value={searchTerm()}
                        onInput={setSearchTerm}
                        placeholder={t('ADMIN_SEARCH_USERS')}
                    />
                </div>

                <Show when={!loading()} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-spinner fa-spin" />
                    </div>
                }>
                    <Show when={filteredUsers().length > 0} fallback={
                        <div class={styles.empty}>
                            <i class="fa-solid fa-user-slash" />
                            <p><Text val="ADMIN_NO_USERS" /></p>
                        </div>
                    }>
                        <div class={styles.list}>
                            <For each={filteredUsers()}>
                                {user => (
                                    <div class={`fcard ${styles.row}`}>
                                        <div class={styles.identity}>
                                            <Show when={user.uuid} fallback={
                                                <div class={styles.avatar} />
                                            }>
                                                <img
                                                    class={styles.avatar}
                                                    src={`https://mineskin.eu/avatar/${user.uuid}/40.png`}
                                                    alt=""
                                                />
                                            </Show>
                                            <div class={styles.names}>
                                                <span class={styles.username}>{user.username}</span>
                                                <span class={styles.idLine}>{user.uuid || user.id}</span>
                                            </div>
                                        </div>
                                        <div class={styles.meta}>
                                            <span class={styles.metaItem} title={t('PERMISSIONS')}>
                                                <i class="fa-solid fa-key" /> {user.permissions ?? 0}
                                            </span>
                                            <span class={user.minecraftUsernameVerified ? `${styles.badge} ${styles.badgeOk}` : `${styles.badge} ${styles.badgeOff}`}>
                                                <i class={user.minecraftUsernameVerified ? "fa-solid fa-circle-check" : "fa-solid fa-circle-xmark"} />
                                                {user.minecraftUsernameVerified ? <Text val="VERIFIED" /> : <Text val="UNVERIFIED" />}
                                            </span>
                                            <button class="btn btn-secondary btn-sm" onClick={() => navigate("/user/" + user.id)}>
                                                <i class="fa-solid fa-user-pen" /> <Text val="EDIT" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </Show>
            </div>
        </div>
    );
}
